
import { useState, useEffect, useCallback } from 'react';
import { canUpdateSunStatus, isOlderThan } from '@/lib/utils/timezoneUtils';
import { SunStatus } from '@/types/terrace';
import { UseSunStatusUpdaterProps, SunStatusUpdaterReturn } from './types';
import { DEFAULT_OPTIONS, UPDATE_DELAYS } from './constants';
import { useRateLimit } from './useRateLimit';
import { useUpdateConditions } from './useUpdateConditions';
import { useSunStatusApi } from './useSunStatusApi';

export const useSunStatusUpdater = ({ 
  terraceId, 
  currentStatus, 
  lastUpdated,
  options = {}
}: UseSunStatusUpdaterProps): SunStatusUpdaterReturn => {
  const mergedOptions = { ...DEFAULT_OPTIONS, ...options };
  const { autoUpdateThresholdMinutes } = mergedOptions;

  const [isUpdating, setIsUpdating] = useState(false);
  const [updatedStatus, setUpdatedStatus] = useState<SunStatus>(currentStatus);
  const [lastChecked, setLastChecked] = useState<string | undefined>(lastUpdated);
  const [error, setError] = useState<string | null>(null);
  
  const rateLimit = useRateLimit(options);
  const { callSunStatusApi } = useSunStatusApi({
    terraceId,
    setIsUpdating,
    setError,
    setUpdatedStatus,
    setLastChecked,
    isUpdating
  });

  // Memoize update conditions with detailed logging
  const updateConditions = useUpdateConditions({
    terraceId,
    lastChecked,
    isUpdating,
    autoUpdateThresholdMinutes,
    checkAutoRateLimit: rateLimit.checkAutoRateLimit
  });

  const updateSunStatus = useCallback(async (isManualTrigger = false) => {
    if (import.meta.env.DEV) console.log(`🌞 ${isManualTrigger ? 'Manual' : 'Auto'} update triggered for terrace:`, terraceId);
    
    // For manual updates, check different rate limiting
    if (isManualTrigger) {
      const manualRateLimitCheck = rateLimit.checkManualRateLimit();
      if (!manualRateLimitCheck.allowed) {
        setError(`Please wait ${manualRateLimitCheck.waitMinutes} more minutes before manual update`);
        return;
      }
      
      // Check time window for manual updates too (but with different message)
      const timeCheck = canUpdateSunStatus('manual-update');
      if (!timeCheck.canUpdate) {
        setError(`Manual updates not available outside 6 AM - 10 PM Stockholm time (current: ${timeCheck.stockholmHour}:00)`);
        return;
      }
      
      rateLimit.recordManualUpdate();
    } else {
      // For auto updates, respect the conditions check
      if (!updateConditions.shouldUpdate) {
        if (import.meta.env.DEV) console.log('🌞 Auto-update skipped:', updateConditions.reason);
        return;
      }
      rateLimit.recordAutoUpdate();
    }
    
    setIsUpdating(true);
    setError(null);
    
    try {
      await callSunStatusApi();
    } catch (error) {
      console.error('🌞 Update failed, but prevented from bubbling up:', error);
    } finally {
      setIsUpdating(false);
    }
  }, [terraceId, updateConditions, rateLimit, callSunStatusApi]);

  // Auto-update effect - only run once when conditions change
  useEffect(() => {
    if (!terraceId) return;
    
    if (updateConditions.shouldUpdate) {
      if (import.meta.env.DEV) console.log('🌞 Scheduling auto-update with 2 second delay...');
      const timer = setTimeout(() => {
        updateSunStatus(false).catch(error => {
          console.error('🌞 Auto-update failed, but prevented from bubbling up:', error);
        });
      }, UPDATE_DELAYS.AUTO_UPDATE);
      
      return () => clearTimeout(timer);
    }
  }, [updateConditions.shouldUpdate, terraceId]);

  // Periodic checking effect (every 10 minutes during sunny hours)
  useEffect(() => {
    if (!terraceId) return;

    const interval = setInterval(() => {
      const timeCheck = canUpdateSunStatus('periodic-check');
      if (timeCheck.canUpdate) {
        if (import.meta.env.DEV) console.log('🌞 Periodic check: evaluating update conditions...');
        // Only check conditions during valid time windows, don't force re-renders
        if (isOlderThan(lastChecked, autoUpdateThresholdMinutes)) {
          if (import.meta.env.DEV) console.log('🌞 Data is old enough, triggering update...');
          updateSunStatus(false).catch(error => {
            console.error('🌞 Periodic update failed:', error);
          });
        }
      }
    }, UPDATE_DELAYS.PERIODIC_CHECK);

    return () => clearInterval(interval);
  }, [terraceId, lastChecked, autoUpdateThresholdMinutes, updateSunStatus]);

  // Reset state when terrace changes
  useEffect(() => {
    if (import.meta.env.DEV) console.log('🌞 Terrace changed, resetting state:', { terraceId, currentStatus, lastUpdated });
    rateLimit.resetRateLimits();
    setUpdatedStatus(currentStatus);
    setLastChecked(lastUpdated);
    setError(null);
  }, [terraceId, currentStatus, lastUpdated, rateLimit]);

  // Manual trigger function with error prevention
  const triggerManualUpdate = useCallback(async () => {
    if (import.meta.env.DEV) console.log('🌞 Manual update triggered by user');
    try {
      await updateSunStatus(true);
    } catch (error) {
      console.error('🌞 Manual update failed, but prevented from bubbling up:', error);
    }
  }, [updateSunStatus]);

  return {
    isUpdating,
    updatedStatus,
    lastChecked: lastChecked || lastUpdated,
    error,
    triggerUpdate: triggerManualUpdate,
    canAutoUpdate: updateConditions.shouldUpdate,
    updateConditions: updateConditions.reason
  };
};
